// Tool surface for the Strudel coding agent.
//
// Phase 1 read-only tools + Phase 2 write tools. Write tools carry a
// `write: true` flag; the streaming loop in client.ts uses it to pause
// for user approval before running the handler.
//
// Handlers never throw. All failures — malformed input, unknown id,
// mini-notation parse error — return `{ error: string }` so the model
// can recover inside the tool-use loop.

import type Anthropic from '@anthropic-ai/sdk';
import { REGISTRY } from '../blocks/registry';
import {
  findEffect,
  findExpression,
  findStatement,
} from '../blocks/walk';
import { inferKey } from '../musicTheory/inferKey';
import type { Numeric } from '../blocks/types';
import type { AgentBridge } from './bridge';
import { summarizeSong } from './projection';
import {
  buildAddComment,
  buildAddEffect,
  buildAddVoice,
  buildApplyVoiceLeading,
  buildHumanizeVoice,
  buildProposeCounterMelody,
  buildRemoveBlock,
  buildRemoveEffect,
  buildRenameConst,
  buildSetPattern,
  buildSetTempo,
  buildSwapRelative,
  buildTransposeVoice,
  buildUpdateEffectArg,
  buildWrapInArrange,
  buildWrapInConditional,
  buildWrapInStack,
  type BuildResult,
} from './writeRecipes';

export type AgentTool = {
  definition: Anthropic.Tool;
  handler: (input: unknown) => unknown;
  /** True for mutating tools — triggers the approval flow in guided mode. */
  write?: boolean;
  /** True for write tools whose mutations land in the Song AST (and
   *  therefore on the undo stack). Transport tools are write tools but
   *  not mutators — their effects are ephemeral audio state. Used by
   *  the chat UI to decide whether Revert should be enabled for a
   *  turn. Single source of truth for both concepts. */
  mutatesSong?: boolean;
};

export function buildTools(bridge: AgentBridge): AgentTool[] {
  return [
    // Read-only
    getSongTool(bridge),
    getBlockTool(bridge),
    getTransportStateTool(bridge),
    listEffectKindsTool(),
    listSampleBanksTool(bridge),
    // Write
    addVoiceTool(bridge),
    removeBlockTool(bridge),
    setPatternTool(bridge),
    addEffectTool(bridge),
    removeEffectTool(bridge),
    updateEffectArgTool(bridge),
    wrapInConditionalTool(bridge),
    wrapInStackTool(bridge),
    wrapInArrangeTool(bridge),
    setTempoTool(bridge),
    renameConstTool(bridge),
    addCommentTool(bridge),
    // Musical (analysis + key/tempo-aware edits)
    analyzeSongTool(bridge),
    transposeVoiceTool(bridge),
    swapToRelativeMinorTool(bridge),
    swapToRelativeMajorTool(bridge),
    humanizeVoiceTool(bridge),
    proposeCounterMelodyTool(bridge),
    applyVoiceLeadingTool(bridge),
    // Transport
    playTool(bridge),
    stopTool(bridge),
    setSoloTool(bridge),
  ];
}

// ─── Shared arg schema fragments ───────────────────────────────────

const ARG_ITEM_SCHEMA = {
  type: 'object',
  description:
    'A single effect argument as a tagged union. kind="num" for numbers (gain, lpf cutoff), kind="str" for strings (scale name, chord symbol, vowel), kind="mini" for a mini-notation pattern (struct, mask), kind="raw" for arbitrary JS expressions.',
  properties: {
    kind: { type: 'string', enum: ['num', 'str', 'mini', 'raw'] },
    value: {
      description:
        "Number when kind='num'; string otherwise. For mini, pass the raw mini-notation source (e.g. \"bd*4\").",
    },
  },
  required: ['kind', 'value'],
  additionalProperties: false,
} as const;

const EFFECT_SPEC_SCHEMA = {
  type: 'object',
  description: 'A simple effect to append to a chain.',
  properties: {
    kind: {
      type: 'string',
      description:
        'SimpleEffectKind — e.g. gain, lpf, rev, scale, chord, room. Conditionals are not allowed here (use wrap_in_conditional).',
    },
    args: {
      type: 'array',
      items: ARG_ITEM_SCHEMA,
      description: 'Effect arguments in order.',
    },
  },
  required: ['kind'],
  additionalProperties: false,
} as const;

// ─── Read-only tools ───────────────────────────────────────────────

function getSongTool(bridge: AgentBridge): AgentTool {
  return {
    definition: {
      name: 'get_song',
      description:
        'Return a compact summary of the entire current Song AST. Use ' +
        'first to orient. Follow up with get_block(id) for full detail.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () => wrap(() => summarizeSong(bridge.getSong())),
  };
}

function getBlockTool(bridge: AgentBridge): AgentTool {
  return {
    definition: {
      name: 'get_block',
      description:
        'Return the full AST subtree for a single block by id. Accepts ' +
        'statement, expression, or effect ids. Returns `{ error }` on miss.',
      input_schema: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Block id.' },
        },
        required: ['id'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const id = (input as { id?: unknown })?.id;
      if (typeof id !== 'string' || !id) {
        return { error: 'id must be a non-empty string' };
      }
      return wrap(() => {
        const song = bridge.getSong();
        const stmt = findStatement(song, id);
        if (stmt) return { kind: 'statement', block: stmt };
        const expr = findExpression(song, id);
        if (expr) return { kind: 'expression', block: expr };
        const eff = findEffect(song, id);
        if (eff) return { kind: 'effect', block: eff };
        return { error: `no block with id ${id}` };
      });
    },
  };
}

function getTransportStateTool(bridge: AgentBridge): AgentTool {
  return {
    definition: {
      name: 'get_transport_state',
      description:
        'Live transport snapshot: playing, phase, cpm/cps, cycle, soloId.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () => wrap(() => bridge.getTransportState()),
  };
}

// The agent-facing category labels mirror the synth-manual sections
// the system prompt uses. They're family names for simple effects,
// plus a `conditional` bucket for body-taking transforms (which all
// share family='structural' but are listed separately so the agent
// sees them as their own group). Derived from REGISTRY at module
// load — adding a new family or kind requires no change here.
const EFFECT_CATEGORY_LABELS = [
  'dynamics',
  'filter',
  'distortion',
  'modulation',
  'time',
  'sample',
  'space',
  'delay',
  'spatial',
  'structural',
  'tonal',
  'conditional',
] as const;

type Category = (typeof EFFECT_CATEGORY_LABELS)[number];

function deriveEffectCategories(): Record<Category, string[]> {
  const out = Object.fromEntries(
    EFFECT_CATEGORY_LABELS.map((l) => [l, [] as string[]]),
  ) as Record<Category, string[]>;
  for (const entry of REGISTRY.values()) {
    if (entry.category === 'conditional') {
      out.conditional.push(entry.kind);
    } else if ((EFFECT_CATEGORY_LABELS as readonly string[]).includes(entry.family)) {
      out[entry.family as Category].push(entry.kind);
    }
  }
  return out;
}

const EFFECT_CATEGORIES = deriveEffectCategories();

function listEffectKindsTool(): AgentTool {
  return {
    definition: {
      name: 'list_effect_kinds',
      description:
        'Enumerate Strudel effect kinds by category. Each kind comes with ' +
        'a one-line hint, its typed arg schema (count + types + per-arg ' +
        'name), and an optional idiomatic example. Omit category for all.',
      input_schema: {
        type: 'object',
        properties: {
          category: {
            type: 'string',
            enum: [...EFFECT_CATEGORY_LABELS],
          },
        },
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const category = (input as { category?: unknown })?.category;
      const annotate = (kinds: readonly string[]) =>
        kinds.map((kind) => {
          const entry = REGISTRY.get(kind);
          const base = { kind, doc: entry?.doc ?? '', args: entry?.args ?? [] };
          return entry?.example
            ? { ...base, example: entry.example }
            : base;
        });
      if (typeof category === 'string' && category in EFFECT_CATEGORIES) {
        return {
          category,
          kinds: annotate(EFFECT_CATEGORIES[category as Category]),
        };
      }
      return {
        categories: Object.entries(EFFECT_CATEGORIES).map(([name, kinds]) => ({
          name,
          kinds: annotate(kinds),
        })),
      };
    },
  };
}

function listSampleBanksTool(bridge: AgentBridge): AgentTool {
  return {
    definition: {
      name: 'list_sample_banks',
      description:
        'List sample banks loaded via samples(…) statements in the buffer.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () => wrap(() => ({ banks: bridge.getSampleBanks() })),
  };
}

// ─── Write tools ───────────────────────────────────────────────────

function addVoiceTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'add_voice',
      description:
        'Add a new voice to the song. kind="voice.s" wraps a pattern with s(…) ' +
        '— use for drum samples (bd cp hh) or named sounds (piano, saw). ' +
        'kind="voice.note" wraps with note(…) — use for pitch-centric lines (a3 c4 e4). ' +
        'Give it a constName to bind it to a name for later stacking/arranging.',
      input_schema: {
        type: 'object',
        properties: {
          kind: { type: 'string', enum: ['voice.s', 'voice.note'] },
          pattern: {
            type: 'string',
            description: 'Mini-notation source, e.g. "bd*4" or "a3 c4 e4".',
          },
          constName: {
            type: 'string',
            description:
              'Optional JS identifier to bind the voice to (e.g. "bass").',
          },
          effects: {
            type: 'array',
            description:
              'Simple effects to apply in order. Conditionals not allowed here.',
            items: EFFECT_SPEC_SCHEMA,
          },
          after: {
            type: 'string',
            description:
              'Insert after this statement id. Omit to append to end.',
          },
        },
        required: ['kind', 'pattern'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(bridge, buildAddVoice(bridge.getSong(), input as never)),
  };
}

function removeBlockTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'remove_block',
      description:
        'Remove any block by id — statement, expression (inside a stack/arrange), ' +
        'effect, or arrange entry. Irreversible within the turn (user can undo ' +
        'via Cmd-Z after apply).',
      input_schema: {
        type: 'object',
        properties: {
          id: { type: 'string' },
        },
        required: ['id'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildRemoveBlock(bridge.getSong(), (input as { id: string }).id),
      ),
  };
}

function setPatternTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'set_pattern',
      description:
        "Replace a voice block's pattern with new mini-notation. Only valid on " +
        'voice.s and voice.note blocks. Parse failures are stored as raw, with ' +
        'the parse error surfaced in the summary.',
      input_schema: {
        type: 'object',
        properties: {
          blockId: { type: 'string' },
          pattern: { type: 'string' },
        },
        required: ['blockId', 'pattern'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const { blockId, pattern } = input as {
        blockId: string;
        pattern: string;
      };
      return applyBuild(bridge, buildSetPattern(bridge.getSong(), blockId, pattern));
    },
  };
}

function addEffectTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'add_effect',
      description:
        'Append a simple effect to the chain of a voice or ref block. ' +
        "For conditionals (every, sometimes, jux), use wrap_in_conditional.",
      input_schema: {
        type: 'object',
        properties: {
          blockId: { type: 'string' },
          kind: {
            type: 'string',
            description: 'SimpleEffectKind, e.g. gain, lpf, room.',
          },
          args: { type: 'array', items: ARG_ITEM_SCHEMA },
          at: {
            type: 'integer',
            description:
              'Optional index to insert at (0-based). Omit to append.',
          },
        },
        required: ['blockId', 'kind'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(bridge, buildAddEffect(bridge.getSong(), input as never)),
  };
}

function removeEffectTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'remove_effect',
      description: 'Remove a single effect by its id.',
      input_schema: {
        type: 'object',
        properties: {
          effectId: { type: 'string' },
        },
        required: ['effectId'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildRemoveEffect(
          bridge.getSong(),
          (input as { effectId: string }).effectId,
        ),
      ),
  };
}

function updateEffectArgTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'update_effect_arg',
      description:
        'Replace one argument of an existing effect — e.g. tweak a cutoff ' +
        'or swap a scale name without rebuilding the effect.',
      input_schema: {
        type: 'object',
        properties: {
          effectId: { type: 'string' },
          argIndex: { type: 'integer', minimum: 0 },
          value: ARG_ITEM_SCHEMA,
        },
        required: ['effectId', 'argIndex', 'value'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildUpdateEffectArg(bridge.getSong(), input as never),
      ),
  };
}

function wrapInConditionalTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'wrap_in_conditional',
      description:
        'Append a ConditionalEffect to a voice or ref. Covers every(n, fn), ' +
        'sometimes(fn), jux(fn), off(t, fn), etc. The body is the method ' +
        'chain applied inside the arrow function.',
      input_schema: {
        type: 'object',
        properties: {
          blockId: { type: 'string' },
          kind: {
            type: 'string',
            description: 'ConditionalKind — every, sometimes, jux, off, etc.',
          },
          condArgs: {
            type: 'array',
            items: ARG_ITEM_SCHEMA,
            description:
              'Leading args before the function — e.g. [4] for every(4, …).',
          },
          body: {
            type: 'array',
            items: EFFECT_SPEC_SCHEMA,
            description:
              'Method chain inside the arrow — e.g. [{kind:"rev"}, {kind:"fast", args:[{kind:"num", value:2}]}].',
          },
        },
        required: ['blockId', 'kind', 'body'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildWrapInConditional(bridge.getSong(), input as never),
      ),
  };
}

function wrapInStackTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'wrap_in_stack',
      description:
        'Create a new stack(…) containing references to named consts. Every ' +
        'statementId must be a const; bare expression statements need ' +
        'rename/binding first.',
      input_schema: {
        type: 'object',
        properties: {
          statementIds: {
            type: 'array',
            items: { type: 'string' },
            minItems: 1,
          },
          constName: { type: 'string' },
        },
        required: ['statementIds'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(bridge, buildWrapInStack(bridge.getSong(), input as never)),
  };
}

function wrapInArrangeTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'wrap_in_arrange',
      description:
        'Create a new arrange(…) that sequences named consts over cycles.',
      input_schema: {
        type: 'object',
        properties: {
          entries: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                statementId: { type: 'string' },
                cycles: { type: 'number' },
              },
              required: ['statementId', 'cycles'],
              additionalProperties: false,
            },
            minItems: 1,
          },
          constName: { type: 'string' },
        },
        required: ['entries'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildWrapInArrange(bridge.getSong(), input as never),
      ),
  };
}

function setTempoTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'set_tempo',
      description:
        'Set the song tempo. kind="setcpm" takes cycles-per-minute (bpm/4 for ' +
        'most styles); kind="setcps" takes cycles-per-second. Replaces any ' +
        'existing setcpm/setcps; inserts at the top if none exists.',
      input_schema: {
        type: 'object',
        properties: {
          kind: { type: 'string', enum: ['setcpm', 'setcps'] },
          value: { type: 'number' },
        },
        required: ['kind', 'value'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const { kind, value } = input as {
        kind: 'setcpm' | 'setcps';
        value: number;
      };
      return applyBuild(bridge, buildSetTempo(bridge.getSong(), kind, value));
    },
  };
}

function renameConstTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'rename_const',
      description:
        'Rename a const and rewire every reference in stacks, arranges, and ' +
        'refs. Safer than remove+add because no refs break.',
      input_schema: {
        type: 'object',
        properties: {
          oldName: { type: 'string' },
          newName: { type: 'string' },
        },
        required: ['oldName', 'newName'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const { oldName, newName } = input as {
        oldName: string;
        newName: string;
      };
      return applyBuild(
        bridge,
        buildRenameConst(bridge.getSong(), oldName, newName),
      );
    },
  };
}

function addCommentTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'add_comment',
      description:
        'Attach a comment to a statement. position="leading" places it above; ' +
        '"trailing" on the same line.',
      input_schema: {
        type: 'object',
        properties: {
          statementId: { type: 'string' },
          text: { type: 'string' },
          position: { type: 'string', enum: ['leading', 'trailing'] },
        },
        required: ['statementId', 'text', 'position'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const { statementId, text, position } = input as {
        statementId: string;
        text: string;
        position: 'leading' | 'trailing';
      };
      return applyBuild(
        bridge,
        buildAddComment(bridge.getSong(), statementId, text, position),
      );
    },
  };
}

/** Best-effort evaluation of a literal Numeric tree to a JS number.
 *  Returns null when the tree can't be statically reduced (refs,
 *  modulators, raw expressions). Used by analyze_song to give the
 *  model a concrete tempo when possible. */
function evalNumeric(n: Numeric): number | null {
  if (n.kind === 'num') return n.value;
  if (n.kind === 'binary') {
    const lhs = evalNumeric(n.lhs);
    const rhs = evalNumeric(n.rhs);
    if (lhs === null || rhs === null) return null;
    if (n.op === '+') return lhs + rhs;
    if (n.op === '-') return lhs - rhs;
    if (n.op === '*') return lhs * rhs;
    if (n.op === '/') return rhs === 0 ? null : lhs / rhs;
  }
  return null;
}

// ─── Musical tools ─────────────────────────────────────────────────

function analyzeSongTool(bridge: AgentBridge): AgentTool {
  return {
    definition: {
      name: 'analyze_song',
      description:
        'Best-effort analysis of the current song: inferred key (root + ' +
        'mode + confidence + sample size), tempo (cpm), and voice count. ' +
        'Returns key=null when no scale or chord effects are present — in ' +
        'that case ask the user before assuming a key.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () =>
      wrap(() => {
        const song = bridge.getSong();
        const tempoStmt = song.statements.find(
          (s) => s.kind === 'setcpm' || s.kind === 'setcps',
        );
        let tempo:
          | { kind: 'setcpm' | 'setcps'; value: number }
          | { kind: 'setcpm' | 'setcps'; expression: 'non-literal' }
          | null = null;
        if (tempoStmt && (tempoStmt.kind === 'setcpm' || tempoStmt.kind === 'setcps')) {
          const evaluated = evalNumeric(tempoStmt.value);
          tempo =
            evaluated !== null
              ? { kind: tempoStmt.kind, value: evaluated }
              : { kind: tempoStmt.kind, expression: 'non-literal' };
        }
        let voiceCount = 0;
        for (const stmt of song.statements) {
          if (stmt.kind !== 'const' && stmt.kind !== 'expression') continue;
          if (stmt.expr.kind === 'voice.s' || stmt.expr.kind === 'voice.note') {
            voiceCount += 1;
          }
        }
        return {
          key: inferKey(song),
          tempo,
          voiceCount,
        };
      }),
  };
}

function transposeVoiceTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'transpose_voice',
      description:
        'Add or replace a .transpose(N) effect on a voice or ref block. ' +
        'Positive N raises pitch; negative lowers. ±12 = an octave. ' +
        'Use this to make a voice "darker" (-12) or "brighter" (+7) ' +
        'without rebuilding it.',
      input_schema: {
        type: 'object',
        properties: {
          blockId: { type: 'string' },
          semitones: { type: 'integer' },
        },
        required: ['blockId', 'semitones'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const { blockId, semitones } = input as {
        blockId: string;
        semitones: number;
      };
      return applyBuild(
        bridge,
        buildTransposeVoice(bridge.getSong(), blockId, semitones),
      );
    },
  };
}

function swapToRelativeMinorTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'swap_to_relative_minor',
      description:
        'Rewrite every .scale("X major") effect in the song to its ' +
        'relative minor (e.g. C major → A minor). Voices already in ' +
        'minor are left alone. Use to make a song feel "darker" without ' +
        'changing notes.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () =>
      applyBuild(bridge, buildSwapRelative(bridge.getSong(), 'minor')),
  };
}

function swapToRelativeMajorTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'swap_to_relative_major',
      description:
        'Rewrite every .scale("X minor") effect to its relative major ' +
        '(e.g. A minor → C major). Voices already in major are left alone. ' +
        'Use to brighten the harmonic feel without re-pitching voices.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () =>
      applyBuild(bridge, buildSwapRelative(bridge.getSong(), 'major')),
  };
}

function humanizeVoiceTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'humanize_voice',
      description:
        'Append .late(rand.range(-jitter, jitter)) to a voice so each ' +
        'event nudges by a small random amount per cycle. Default jitter ' +
        '0.005 (≈12ms at 124 cpm). Use to soften a mechanical-feeling ' +
        'drum or arp.',
      input_schema: {
        type: 'object',
        properties: {
          blockId: { type: 'string' },
          jitter: {
            type: 'number',
            description:
              'Max jitter in cycles. Default 0.005, hard cap 0.05.',
          },
        },
        required: ['blockId'],
        additionalProperties: false,
      },
    },
    handler: (input) => {
      const { blockId, jitter } = input as {
        blockId: string;
        jitter?: number;
      };
      return applyBuild(
        bridge,
        buildHumanizeVoice(bridge.getSong(), blockId, jitter),
      );
    },
  };
}

function proposeCounterMelodyTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'propose_counter_melody',
      description:
        'Generate a complementary note() voice by transposing the source ' +
        "voice's pitch atoms by intervalSemitones (default 4 = major " +
        'third; pass 3 for minor third, 7 for fifth). Source must be a ' +
        'top-level voice.note with a structured mini pattern. The new ' +
        'voice is inserted as a const right after the source.',
      input_schema: {
        type: 'object',
        properties: {
          sourceBlockId: { type: 'string' },
          constName: {
            type: 'string',
            description: 'Const name for the new voice — required for stacking.',
          },
          intervalSemitones: {
            type: 'integer',
            description:
              'Semitone shift. Default 4. Suggested: 3 (minor 3rd), 4 ' +
              '(major 3rd), 7 (perfect 5th), -12 (octave below).',
          },
        },
        required: ['sourceBlockId', 'constName'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildProposeCounterMelody(bridge.getSong(), input as never),
      ),
  };
}

function applyVoiceLeadingTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    mutatesSong: true,
    definition: {
      name: 'apply_voice_leading',
      description:
        'Append .voicing() after .chord(...) on a voice — Strudel will ' +
        'pick chord inversions that minimise pitch movement between ' +
        'successive chords. Errors if the block has no .chord() effect ' +
        'or already has .voicing().',
      input_schema: {
        type: 'object',
        properties: {
          blockId: { type: 'string' },
        },
        required: ['blockId'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyBuild(
        bridge,
        buildApplyVoiceLeading(
          bridge.getSong(),
          (input as { blockId: string }).blockId,
        ),
      ),
  };
}

// ─── Transport tools ───────────────────────────────────────────────

function playTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    definition: {
      name: 'play',
      description:
        'Evaluate the current code and start playback. The user must have ' +
        'clicked Play once to boot the engine; this tool errors otherwise. ' +
        'Call after a batch of edits to hear the result.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () => applyTransport(bridge.play(), 'playing'),
  };
}

function stopTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    definition: {
      name: 'stop',
      description: 'Halt playback and silence audio.',
      input_schema: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
    },
    handler: () => applyTransport(bridge.stop(), 'stopped'),
  };
}

function setSoloTool(bridge: AgentBridge): AgentTool {
  return {
    write: true,
    definition: {
      name: 'set_solo',
      description:
        'Set the single-block solo to audition one voice in isolation. ' +
        'Pass blockId=null to clear. Only one block can be soloed at a time. ' +
        'Block must be a voice or named ref.',
      input_schema: {
        type: 'object',
        properties: {
          blockId: {
            type: ['string', 'null'],
            description: 'Block id to solo, or null to clear.',
          },
        },
        required: ['blockId'],
        additionalProperties: false,
      },
    },
    handler: (input) =>
      applyTransport(
        bridge.setSolo(
          (input as { blockId: string | null }).blockId ?? null,
        ),
        'solo set',
      ),
  };
}

function applyTransport(
  result: { ok: true } | { ok: false; reason: string },
  summary: string,
): unknown {
  if (!result.ok) return { error: result.reason };
  return { applied: true, summary };
}

// ─── Apply helper ──────────────────────────────────────────────────

function applyBuild(bridge: AgentBridge, build: BuildResult): unknown {
  if (!build.ok) return { error: build.reason };
  const apply = bridge.applyRecipe(build.recipe);
  if (!apply.ok) return { error: `apply failed: ${apply.reason}` };
  return {
    applied: true,
    summary: build.summary,
    ...(build.outputs ?? {}),
  };
}

function wrap<T>(fn: () => T): T | { error: string } {
  try {
    return fn();
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}
